// Immediate fix for document visibility issue
// This script ensures documents can be found regardless of ID mismatches

/**
 * Fix document visibility by creating proper employee mappings
 */
export const fixDocumentVisibility = () => {
    console.log('=== FIXING DOCUMENT VISIBILITY ===');

    // Step 1: Get the current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('Current user:', currentUser);

    // Step 2: Get all employees
    const employees = JSON.parse(localStorage.getItem('hrportal_employees') || '[]');

    // Step 3: Find ALL documents in localStorage
    const allKeys = Object.keys(localStorage);
    const docKeys = allKeys.filter(key => key.startsWith('employeeDocuments_'));

    console.log('Found document keys:', docKeys);

    // Step 4: Collect ALL documents
    let allDocuments = [];
    docKeys.forEach(key => {
        const docs = JSON.parse(localStorage.getItem(key) || '[]');
        if (docs.length > 0) {
            console.log(`Key ${key} has ${docs.length} documents`);
            allDocuments = [...allDocuments, ...docs];
        }
    });

    console.log('Total documents found:', allDocuments.length);

    // Step 5: If user is logged in, copy ALL company documents to their ID
    if (currentUser.email) {
        // Find matching employee
        const matchingEmployee = employees.find(emp =>
            emp.email === currentUser.email ||
            emp.work_email === currentUser.email ||
            emp.loginCredentials?.loginId === currentUser.email
        );

        if (matchingEmployee) {
            console.log('Found matching employee:', matchingEmployee);

            // Get company documents (sent by admin)
            const companyDocs = allDocuments.filter(doc =>
                doc.type === 'company' ||
                doc.sentBy === 'HR Department' ||
                doc.sentBy ||
                doc.receivedAt
            );

            console.log('Company documents to copy:', companyDocs.length);

            // Save to ALL possible keys for this user
            const keysToSave = [
                `employeeDocuments_${matchingEmployee.id}`,
                `employeeDocuments_${matchingEmployee.employee_id}`,
                `employeeDocuments_${matchingEmployee.employee_code}`,
                `employeeDocuments_${matchingEmployee.email}`,
                `employeeDocuments_${currentUser.id}`,
                `employeeDocuments_${currentUser.email}`
            ].filter(Boolean);

            // Remove duplicates
            const uniqueKeys = [...new Set(keysToSave)];

            uniqueKeys.forEach(key => {
                if (companyDocs.length > 0) {
                    localStorage.setItem(key, JSON.stringify(companyDocs));
                    console.log(`✓ Saved ${companyDocs.length} documents to ${key}`);
                }
            });

            // Also update the user object to have consistent IDs
            const updatedUser = {
                ...currentUser,
                id: matchingEmployee.id,
                employee_id: matchingEmployee.id,
                employee_code: matchingEmployee.employee_code
            };

            localStorage.setItem('user', JSON.stringify(updatedUser));
            console.log('✓ Updated user object with consistent IDs');

            return {
                fixed: true,
                documentsFound: companyDocs.length,
                message: `Fixed! ${companyDocs.length} documents now available`
            };
        } else {
            console.log('⚠️ No matching employee found for current user');

            // Still try to get documents by email
            const emailKey = `employeeDocuments_${currentUser.email}`;
            const companyDocs = allDocuments.filter(doc =>
                doc.type === 'company' || doc.sentBy
            );

            if (companyDocs.length > 0) {
                localStorage.setItem(emailKey, JSON.stringify(companyDocs));
                console.log(`✓ Saved ${companyDocs.length} documents to email key`);
            }
        }
    }

    return {
        fixed: false,
        documentsFound: allDocuments.length,
        message: 'Check console for details'
    };
};

/**
 * Create a universal document store that works regardless of IDs
 */
export const createUniversalDocumentStore = () => {
    console.log('=== Creating Universal Document Store ===');

    // Collect ALL documents from all keys
    const allKeys = Object.keys(localStorage);
    const docKeys = allKeys.filter(key => key.startsWith('employeeDocuments_'));

    let allDocuments = [];
    const documentMap = new Map();

    docKeys.forEach(key => {
        const docs = JSON.parse(localStorage.getItem(key) || '[]');
        docs.forEach(doc => {
            // Use document ID to avoid duplicates
            if (!documentMap.has(doc.id)) {
                documentMap.set(doc.id, doc);
            }
        });
    });

    allDocuments = Array.from(documentMap.values());

    // Save to a universal key
    localStorage.setItem('universalDocuments', JSON.stringify(allDocuments));

    console.log(`✓ Created universal store with ${allDocuments.length} documents`);

    // Also save company documents separately
    const companyDocs = allDocuments.filter(doc =>
        doc.type === 'company' || doc.sentBy
    );

    localStorage.setItem('universalCompanyDocuments', JSON.stringify(companyDocs));

    console.log(`✓ ${companyDocs.length} company documents in universal store`);

    return {
        total: allDocuments.length,
        company: companyDocs.length
    };
};

/**
 * Ensure document is saved for specific employee email
 */
export const ensureDocumentForEmployee = (employeeEmail, document) => {
    console.log(`Ensuring document for ${employeeEmail}`);

    // Find employee by email
    const employees = JSON.parse(localStorage.getItem('hrportal_employees') || '[]');
    const employee = employees.find(emp =>
        emp.email === employeeEmail ||
        emp.work_email === employeeEmail ||
        emp.loginCredentials?.loginId === employeeEmail
    );

    if (employee) {
        // Save to all possible keys
        const keys = [
            `employeeDocuments_${employee.id}`,
            `employeeDocuments_${employee.employee_id}`,
            `employeeDocuments_${employee.employee_code}`,
            `employeeDocuments_${employeeEmail}`,
            `employeeDocuments_${employee.email}`
        ].filter(Boolean);

        const uniqueKeys = [...new Set(keys)];

        uniqueKeys.forEach(key => {
            const existing = JSON.parse(localStorage.getItem(key) || '[]');
            const exists = existing.some(d => d.id === document.id);

            if (!exists) {
                existing.push(document);
                localStorage.setItem(key, JSON.stringify(existing));
                console.log(`✓ Saved document to ${key}`);
            }
        });

        return true;
    }

    return false;
};

// Auto-run fixes on load
if (typeof window !== 'undefined') {
    window.fixDocumentVisibility = fixDocumentVisibility;
    window.createUniversalDocumentStore = createUniversalDocumentStore;
    window.ensureDocumentForEmployee = ensureDocumentForEmployee;

    console.log('%c🔧 Document Fix Utilities Loaded', 'color: blue; font-weight: bold');
    console.log('Available commands:');
    console.log('  fixDocumentVisibility() - Fix current user\'s documents');
    console.log('  createUniversalDocumentStore() - Create universal document store');
    console.log('  ensureDocumentForEmployee(email, doc) - Save doc for specific employee');
}

const documentIssueHelper = {
    fixDocumentVisibility,
    createUniversalDocumentStore,
    ensureDocumentForEmployee
};

export default documentIssueHelper;